import { useState, useCallback, useRef, useEffect } from 'preact/hooks'
import {
  Room,
  RoomEvent,
  Track,
  ConnectionState,
  ConnectionQuality,
  VideoPresets,
} from 'livekit-client'
import api from '../a-portal/api'

const LIVEKIT_WS_URL =
  process.env.LIVEKIT_WS_URL || 'wss://livekit.mehrab-alquran.com'

function mapConnectionQuality(quality) {
  switch (quality) {
  case ConnectionQuality.Excellent:
    return 'excellent'
  case ConnectionQuality.Good:
    return 'good'
  case ConnectionQuality.Poor:
  case ConnectionQuality.Lost:
    return 'poor'
  default:
    return 'good'
  }
}

/**
 * Data message types for admin features
 */
const DATA_MSG_CHAT = 'chat'
const DATA_MSG_ADMIN_KICK = 'admin:kick'
const DATA_MSG_ADMIN_MUTE = 'admin:mute'
const DATA_MSG_ADMIN_RESTRICT_UNMUTE = 'admin:restrictUnmute'

export function useLiveKit(options = {}) {
  const {
    onParticipantJoined,
    onParticipantLeft,
    onError,
    onCallEnded,
    onConnectionSuccess,
    onNetworkQualityChanged,
    onConnectionRecovering,
    onDataReceived,
    onKickedOut,
  } = options

  const roomRef = useRef(null)
  const qualityIntervalRef = useRef(null)
  const lastQualityRef = useRef(ConnectionQuality.Excellent)
  const virtualCountRef = useRef(0)

  const [isConnected, setIsConnected] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [connectionState, setConnectionState] = useState(
    ConnectionState.Disconnected,
  )
  const [error, setError] = useState(null)

  const [isMicMuted, setIsMicMuted] = useState(false)
  const [isVideoEnabled, setIsVideoEnabled] = useState(false)
  const [isScreenSharing, setIsScreenSharing] = useState(false)

  const [localVideoTrack, setLocalVideoTrack] = useState(null)
  const [localAudioTrack, setLocalAudioTrack] = useState(null)
  const [localScreenTrack, setLocalScreenTrack] = useState(null)

  const [participants, setParticipants] = useState([])
  const [chatMessages, setChatMessages] = useState([])
  const [callQuality, setCallQuality] = useState(null)
  const [localIdentity, setLocalIdentity] = useState(null)
  const [unmuteRestricted, setUnmuteRestricted] = useState(false)

  const updateParticipantsList = useCallback(() => {
    const room = roomRef.current
    if (!room) return
    const list = []
    for (const [, participant] of room.remoteParticipants) {
      const videoTrack = participant.getTrackPublication(
        Track.Source.Camera,
      )
      const audioTrack = participant.getTrackPublication(
        Track.Source.Microphone,
      )
      const screenTrack = participant.getTrackPublication(
        Track.Source.ScreenShare,
      )
      list.push({
        identity: participant.identity,
        name: participant.name || participant.identity,
        isMuted:
          !audioTrack || audioTrack.isMuted || !audioTrack.isSubscribed,
        isVideoEnabled:
          !!videoTrack && !videoTrack.isMuted && videoTrack.isSubscribed,
        isScreenSharing:
          !!screenTrack &&
          !screenTrack.isMuted &&
          screenTrack.isSubscribed,
        isSpeaking: participant.isSpeaking,
        videoTrack:
          videoTrack && videoTrack.track ? videoTrack.track : null,
        audioTrack:
          audioTrack && audioTrack.track ? audioTrack.track : null,
        screenTrack:
          screenTrack && screenTrack.track ? screenTrack.track : null,
        connectionQuality: mapConnectionQuality(
          participant.connectionQuality,
        ),
      })
    }
    setParticipants(list)
  }, [])

  const startQualityMonitoring = useCallback(() => {
    if (qualityIntervalRef.current)
      clearInterval(qualityIntervalRef.current)
    qualityIntervalRef.current = setInterval(() => {
      const room = roomRef.current
      if (!room || !room.localParticipant) return
      const quality = room.localParticipant.connectionQuality
      const mapped = mapConnectionQuality(quality)
      if (quality !== lastQualityRef.current) {
        lastQualityRef.current = quality
        setCallQuality(mapped)
        if (onNetworkQualityChanged) onNetworkQualityChanged(mapped)
      }
    }, 2000)
  }, [onNetworkQualityChanged])

  const enableCamera = async (room) => {
    try {
      await room.localParticipant.setCameraEnabled(true)
      setIsVideoEnabled(true)
      await new Promise(r => setTimeout(r, 200))
      const videoPub = room.localParticipant.getTrackPublication(Track.Source.Camera)

      if (videoPub && videoPub.track) {
        setLocalVideoTrack(videoPub.track)
        return
      }

      console.warn('Camera track not found after setCameraEnabled, retrying...')
      await new Promise(r => setTimeout(r, 500))
      const retryPub = room.localParticipant.getTrackPublication(Track.Source.Camera)
      if (retryPub && retryPub.track) {
        setLocalVideoTrack(retryPub.track)
      }
    } catch (camError) {
      console.warn('Could not enable camera:', camError)
      await retryCameraEnable(room)
    }
  }

  const retryCameraEnable = async (room) => {
    try {
      await new Promise(r => setTimeout(r, 1000))
      await room.localParticipant.setCameraEnabled(true)
      setIsVideoEnabled(true)
      const videoPub = room.localParticipant.getTrackPublication(Track.Source.Camera)
      if (videoPub && videoPub.track) {
        setLocalVideoTrack(videoPub.track)
      }
    } catch (retryError) {
      console.warn('Camera retry also failed:', retryError)
    }
  }

  const enableMicrophone = async (room) => {
    try {
      await room.localParticipant.setMicrophoneEnabled(true)
      setIsMicMuted(false)
      const audioPub = room.localParticipant.getTrackPublication(
        Track.Source.Microphone,
      )
      if (audioPub && audioPub.track)
        setLocalAudioTrack(audioPub.track)
    } catch (micError) {
      console.warn('Could not enable microphone:', micError)
    }
  }

  const handleDataMessage = useCallback((message, room) => {
    if (message.type === DATA_MSG_CHAT) {
      setChatMessages((prev) => [
        ...prev,
        {
          sender: message.sender,
          senderName: message.senderName,
          text: message.text,
          timestamp: message.timestamp,
          isLocal: false,
        },
      ])
      return
    }

    if (message.type === DATA_MSG_ADMIN_KICK) {
      if (message.targetIdentity === room.localParticipant.identity) {
        console.warn('[LiveKit] You have been kicked from the call')
        room.disconnect()
        if (onKickedOut) onKickedOut()
      }
      return
    }

    if (message.type === DATA_MSG_ADMIN_MUTE) {
      if (message.targetIdentity === room.localParticipant.identity) {
        console.warn('[LiveKit] You have been muted by admin')
        room.localParticipant.setMicrophoneEnabled(false)
        setIsMicMuted(true)
        setLocalAudioTrack(null)
      }
      return
    }

    if (message.type === DATA_MSG_ADMIN_RESTRICT_UNMUTE) {
      if (message.targetIdentity === room.localParticipant.identity) {
        setUnmuteRestricted(message.restricted)
      }
    }
  }, [onKickedOut])

  const sendChatMessage = useCallback((text) => {
    const room = roomRef.current
    if (!room || !room.localParticipant) return
    const encoder = new TextEncoder()
    const data = encoder.encode(
      JSON.stringify({
        type: DATA_MSG_CHAT,
        sender: room.localParticipant.identity,
        senderName:
          room.localParticipant.name ||
          room.localParticipant.identity,
        text,
        timestamp: Date.now(),
      }),
    )
    room.localParticipant.publishData(data, { reliable: true })
    setChatMessages((prev) => [
      ...prev,
      {
        sender: room.localParticipant.identity,
        senderName:
          room.localParticipant.name ||
          room.localParticipant.identity,
        text,
        timestamp: Date.now(),
        isLocal: true,
      },
    ])
  }, [])

  const connect = useCallback(
    async (roomName, callType, displayName, mediaOptions = {}) => {
      if (isConnecting || isConnected) return

      const shouldEnableMic = mediaOptions.micOn !== undefined ? mediaOptions.micOn : true
      const shouldEnableCamera = mediaOptions.cameraOn !== undefined
        ? mediaOptions.cameraOn
        : (callType === 'video')

      setIsConnecting(true)
      setError(null)

      try {
        const tokenData = await api.getLiveKitToken(roomName, displayName)
        if (!tokenData || !tokenData.token) {
          throw new Error('Failed to get connection token')
        }

        const { token, wsUrl } = tokenData
        const livekitUrl = wsUrl || LIVEKIT_WS_URL

        const room = new Room({
          adaptiveStream: true,
          dynacast: true,
          audioCaptureDefaults: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
          videoCaptureDefaults: {
            resolution: VideoPresets.h720.resolution,
            facingMode: 'user',
          },
          publishDefaults: {
            videoEncoding: {
              maxBitrate: 1_500_000,
              maxFramerate: 30,
            },
          },
        })
        roomRef.current = room

        room.on(RoomEvent.ConnectionStateChanged, (state) => {
          setConnectionState(state)
          switch (state) {
          case ConnectionState.Connected:
            setIsConnected(true)
            setIsConnecting(false)
            if (onConnectionSuccess) onConnectionSuccess()
            break
          case ConnectionState.Reconnecting:
            if (onConnectionRecovering) onConnectionRecovering()
            break
          case ConnectionState.Disconnected:
            setIsConnected(false)
            setIsConnecting(false)
            break
          }
        })

        room.on(RoomEvent.ParticipantConnected, (participant) => {
          updateParticipantsList()
          if (onParticipantJoined)
            onParticipantJoined(participant.identity)
        })

        room.on(RoomEvent.ParticipantDisconnected, (participant) => {
          updateParticipantsList()
          if (onParticipantLeft)
            onParticipantLeft(participant.identity)
        })

        room.on(RoomEvent.TrackSubscribed, () => {
          updateParticipantsList()
        })
        room.on(RoomEvent.TrackUnsubscribed, () => {
          updateParticipantsList()
        })
        room.on(RoomEvent.TrackMuted, () => {
          updateParticipantsList()
        })
        room.on(RoomEvent.TrackUnmuted, () => {
          updateParticipantsList()
        })

        room.on(RoomEvent.ActiveSpeakersChanged, () => {
          updateParticipantsList()
        })

        room.on(RoomEvent.LocalTrackPublished, (publication) => {
          if (publication.track) {
            if (
              publication.kind === Track.Kind.Video &&
              publication.source === Track.Source.Camera
            ) {
              setLocalVideoTrack(publication.track)
              setIsVideoEnabled(true)
            } else if (
              publication.kind === Track.Kind.Video &&
              publication.source === Track.Source.ScreenShare
            ) {
              setLocalScreenTrack(publication.track)
              setIsScreenSharing(true)
            } else if (publication.kind === Track.Kind.Audio) {
              setLocalAudioTrack(publication.track)
            }
          }
        })

        room.on(RoomEvent.LocalTrackUnpublished, (publication) => {
          if (
            publication.kind === Track.Kind.Video &&
            publication.source === Track.Source.Camera
          ) {
            setLocalVideoTrack(null)
            setIsVideoEnabled(false)
          } else if (
            publication.kind === Track.Kind.Video &&
            publication.source === Track.Source.ScreenShare
          ) {
            setLocalScreenTrack(null)
            setIsScreenSharing(false)
          } else if (publication.kind === Track.Kind.Audio) {
            setLocalAudioTrack(null)
          }
        })

        room.on(RoomEvent.DataReceived, (payload, participant) => {
          try {
            const decoder = new TextDecoder()
            const message = JSON.parse(decoder.decode(payload))
            handleDataMessage(message, room)
            if (onDataReceived) onDataReceived(message, participant)
          } catch (e) {
            /* ignore malformed data */
          }
        })

        room.on(RoomEvent.MediaDevicesError, (err) => {
          const errorMsg = `Media device error: ${err.message}`
          setError(errorMsg)
          if (onError) onError(errorMsg)
        })

        room.on(RoomEvent.Disconnected, () => {
          setIsConnected(false)
        })

        await room.connect(livekitUrl, token)
        setLocalIdentity(tokenData.identity)

        // Enable microphone based on user's lobby choice
        if (shouldEnableMic) {
          await enableMicrophone(room)
        } else {
          setIsMicMuted(true)
        }

        // Enable camera based on user's lobby choice
        if (shouldEnableCamera) {
          await enableCamera(room)
        } else {
          setIsVideoEnabled(false)
        }

        // Load existing remote participants
        updateParticipantsList()
        startQualityMonitoring()
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : 'Failed to connect'
        setError(errorMsg)
        if (onError) onError(errorMsg)
        setIsConnecting(false)

        if (roomRef.current) {
          try {
            await roomRef.current.disconnect()
          } catch (_) {
            console.log('error')
          }
          roomRef.current = null
        }
      }
    },
    [
      isConnecting,
      isConnected,
      updateParticipantsList,
      startQualityMonitoring,
      onConnectionSuccess,
      onConnectionRecovering,
      onParticipantJoined,
      onParticipantLeft,
      onError,
      onDataReceived,
    ],
  )

  const disconnect = useCallback(async () => {
    if (qualityIntervalRef.current) {
      clearInterval(qualityIntervalRef.current)
      qualityIntervalRef.current = null
    }
    const room = roomRef.current
    if (room) {
      room.removeAllListeners()
      if (isVideoEnabled && room.localParticipant) {
        await room.localParticipant.setCameraEnabled(false)
      }
      await room.disconnect()
    }
    setIsConnected(false)
    setConnectionState(ConnectionState.Disconnected)
    setLocalVideoTrack(null)
    setLocalAudioTrack(null)
    setLocalScreenTrack(null)
    setParticipants([])
    setIsMicMuted(false)
    setIsVideoEnabled(false)
    setIsScreenSharing(false)
    setChatMessages([])
    setCallQuality(null)
    roomRef.current = null
    if (onCallEnded) onCallEnded()
  }, [isVideoEnabled, onCallEnded])

  const toggleMute = useCallback(async () => {
    const room = roomRef.current
    if (!room || !room.localParticipant) return
    // Read current mic state directly from the room to avoid stale closure
    const currentlyEnabled = room.localParticipant.isMicrophoneEnabled
    const newEnabled = !currentlyEnabled

    // If admin restricted unmute, prevent it
    if (newEnabled && unmuteRestricted) {
      console.warn('[LiveKit] Unmute restricted by admin')
      return
    }

    try {
      await room.localParticipant.setMicrophoneEnabled(newEnabled)
    } catch (e) {
      console.warn('setMicrophoneEnabled failed:', e)
    }
    // Also directly control the MediaStreamTrack as a safety measure
    const audioPub = room.localParticipant.getTrackPublication(
      Track.Source.Microphone,
    )
    if (audioPub && audioPub.track) {
      const mediaTrack = audioPub.track.mediaStreamTrack
      if (mediaTrack) {
        mediaTrack.enabled = newEnabled
      }
      if (newEnabled) {
        setLocalAudioTrack(audioPub.track)
      } else {
        setLocalAudioTrack(null)
      }
    } else if (!newEnabled) {
      setLocalAudioTrack(null)
    }
    setIsMicMuted(!newEnabled)
  }, [unmuteRestricted])

  const toggleVideo = useCallback(async () => {
    const room = roomRef.current
    if (!room || !room.localParticipant) return
    // Read current camera state directly from the room to avoid stale closure
    const currentlyEnabled = room.localParticipant.isCameraEnabled
    const newEnabled = !currentlyEnabled
    await room.localParticipant.setCameraEnabled(newEnabled)
    if (newEnabled) {
      const videoPub = room.localParticipant.getTrackPublication(
        Track.Source.Camera,
      )
      if (videoPub && videoPub.track)
        setLocalVideoTrack(videoPub.track)
    } else {
      setLocalVideoTrack(null)
    }
    setIsVideoEnabled(newEnabled)
  }, [])

  const toggleScreenShare = useCallback(async () => {
    const room = roomRef.current
    if (!room || !room.localParticipant) return
    // Read current screen share state from the room to avoid stale closure
    const screenPub = room.localParticipant.getTrackPublication(
      Track.Source.ScreenShare,
    )
    const currentlySharing = !!screenPub && !screenPub.isMuted
    const newEnabled = !currentlySharing
    try {
      await room.localParticipant.setScreenShareEnabled(newEnabled, {
        // Exclude the current tab to prevent infinite mirror loop
        selfBrowserSurface: 'exclude',
        surfaceSwitching: 'include',
        systemAudio: 'exclude',
        preferCurrentTab: false,
      })
      // State will be updated by LocalTrackPublished/Unpublished handlers
    } catch (err) {
      // User cancelled the screen share picker — reset state
      console.warn('Screen share toggle error:', err)
      setIsScreenSharing(false)
      setLocalScreenTrack(null)
    }
  }, [])

  const endCall = useCallback(async () => {
    await disconnect()
  }, [disconnect])

  // ----- Admin: Kick participant via data channel -----
  const kickParticipant = useCallback((targetIdentity) => {
    const room = roomRef.current
    if (!room || !room.localParticipant) return
    const encoder = new TextEncoder()
    const data = encoder.encode(JSON.stringify({
      type: DATA_MSG_ADMIN_KICK,
      targetIdentity,
      adminIdentity: room.localParticipant.identity,
      timestamp: Date.now(),
    }))
    room.localParticipant.publishData(data, { reliable: true })
  }, [])

  // ----- Admin: Mute a participant via data channel -----
  const muteParticipant = useCallback((targetIdentity) => {
    const room = roomRef.current
    if (!room || !room.localParticipant) return
    const encoder = new TextEncoder()
    const data = encoder.encode(JSON.stringify({
      type: DATA_MSG_ADMIN_MUTE,
      targetIdentity,
      adminIdentity: room.localParticipant.identity,
      timestamp: Date.now(),
    }))
    room.localParticipant.publishData(data, { reliable: true })
  }, [])

  // ----- Admin: Restrict unmute for a participant -----
  const restrictUnmute = useCallback((targetIdentity, restricted = true) => {
    const room = roomRef.current
    if (!room || !room.localParticipant) return
    const encoder = new TextEncoder()
    const data = encoder.encode(JSON.stringify({
      type: DATA_MSG_ADMIN_RESTRICT_UNMUTE,
      targetIdentity,
      restricted,
      adminIdentity: room.localParticipant.identity,
      timestamp: Date.now(),
    }))
    room.localParticipant.publishData(data, { reliable: true })
  }, [])

  // ----- Virtual participant for testing -----
  const addVirtualParticipant = useCallback(async () => {
    const room = roomRef.current
    if (!room) return
    const roomName = room.name
    virtualCountRef.current += 1
    const vName = `Virtual User ${virtualCountRef.current}`
    try {
      await api.getLiveKitToken(roomName, vName)
      // The token request creates a phantom participant on the server side.
      // For real virtual participants we'd need a bot service; here we just
      // inject a placeholder into the participants list for UI testing.
      setParticipants(prev => [
        ...prev,
        {
          identity: `virtual-${virtualCountRef.current}-${Date.now()}`,
          name: vName,
          isMuted: false,
          isVideoEnabled: false,
          isScreenSharing: false,
          isSpeaking: false,
          videoTrack: null,
          audioTrack: null,
          screenTrack: null,
          connectionQuality: 'excellent',
          isVirtual: true,
        },
      ])
    } catch (err) {
      console.warn('Failed to add virtual participant:', err)
    }
  }, [])

  useEffect(() => {
    return () => {
      if (qualityIntervalRef.current)
        clearInterval(qualityIntervalRef.current)
      if (roomRef.current) roomRef.current.disconnect()
    }
  }, [])

  return {
    // Connection state
    isConnected,
    isConnecting,
    connectionState,
    error,
    localIdentity,

    // Local media state
    isMicMuted,
    isVideoEnabled,
    isScreenSharing,
    localVideoTrack,
    localAudioTrack,
    localScreenTrack,
    unmuteRestricted,

    // Remote participants
    participants,

    // Chat
    chatMessages,
    sendChatMessage,

    // Quality
    callQuality,

    // Room reference
    room: roomRef.current,

    // Actions
    connect,
    disconnect,
    toggleMute,
    toggleVideo,
    toggleScreenShare,
    endCall,

    // Admin actions
    kickParticipant,
    muteParticipant,
    restrictUnmute,
    addVirtualParticipant,
  }
}
